// Utility functions for managing day completion status in local storage

import { BadgeType, BadgeTier, BADGE_REQUIREMENTS, getBadgeTier, setBadgeTier, getTierLevel } from './badges';

const STORAGE_KEY = 'advent_calendar_completions';
const SCORE_KEY = 'advent_calendar_score';
const LAST_UNLOCKED_DAY_KEY = 'advent_calendar_last_unlocked_day';
const DAY_SUBMISSIONS_KEY = 'advent_calendar_day_submissions';
const COMPLETION_DATES_KEY = 'advent_calendar_completion_dates';
const ON_TIME_COMPLETIONS_KEY = 'advent_calendar_on_time_completions';
const BADGES_KEY = 'advent_calendar_badges';
export const INFO_POPUP_WELCOME_KEY = 'info_popup_welcome_shown';
export const INFO_POPUP_LAST_DAY_KEY = 'info_popup_last_day_shown';
const POINTS_PER_DAY = 1000;

interface CompletionData {
  [dayIndex: number]: boolean;
}

interface ScoreBreakdown {
  relevance: number;
  correctness: number;
  depth: number;
  clarity: number;
  promptcraft: number;
}

interface Feedback {
  improvements?: string[];
  strengths?: string[];
  summary?: string;
}

interface DaySubmission {
  score: number;
  stars: number;
  scoreBreakdown: ScoreBreakdown;
  feedback?: Feedback;
}

interface DaySubmissionsData {
  [dayIndex: number]: DaySubmission;
}

interface CompletionDatesData {
  [dayIndex: number]: string; // ISO date string
}

interface NewBadge {
  type: BadgeType;
  tier: BadgeTier;
}

interface BadgeCheckResult {
  newBadges: NewBadge[];
}

// Get all day submissions
const getDaySubmissionsData = (): DaySubmissionsData => {
  try {
    const stored = localStorage.getItem(DAY_SUBMISSIONS_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Error reading day submissions data:', error);
    return {};
  }
};

// Get submission for a specific day
const getDaySubmission = (dayIndex: number): DaySubmission | null => {
  const submissions = getDaySubmissionsData();
  return submissions[dayIndex] || null;
};

// Export for external use
export const getDaySubmissionData = (dayIndex: number): DaySubmission | null => {
  return getDaySubmission(dayIndex);
};

// Save submission data without marking as completed (for 0-star submissions)
export const saveSubmissionData = (dayIndex: number, score: number, stars: number, breakdown: ScoreBreakdown, feedback?: Feedback): void => {
  const previousSubmission = getDaySubmission(dayIndex);

  // Only save if there's no existing submission or if the new score is better
  if (!previousSubmission || score > (previousSubmission.score || 0)) {
    saveDaySubmission(dayIndex, {
      score,
      stars,
      scoreBreakdown: breakdown,
      feedback
    });
  }
};

// Save/update submission for a day
const saveDaySubmission = (dayIndex: number, submission: DaySubmission): void => {
  try {
    const submissions = getDaySubmissionsData();
    submissions[dayIndex] = submission;
    localStorage.setItem(DAY_SUBMISSIONS_KEY, JSON.stringify(submissions));
  } catch (error) {
    console.error('Error saving day submission:', error);
  }
};

export const getCompletedDays = (): CompletionData => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Error reading completion data:', error);
    return {};
  }
};

export const isDayCompleted = (dayIndex: number): boolean => {
  const completed = getCompletedDays();
  return completed[dayIndex] === true;
};

export const getDayScore = (dayIndex: number): number => {
  const submission = getDaySubmission(dayIndex);
  return submission?.score || 0;
};

const getDayBreakdown = (dayIndex: number): ScoreBreakdown | null => {
  const submission = getDaySubmission(dayIndex);
  return submission?.scoreBreakdown || null;
};

interface MarkDayCompletedResult {
  scoreDelta: number;
  previousBreakdown: ScoreBreakdown | null;
}

export const markDayCompleted = (dayIndex: number, totalPoints?: number, stars?: number, breakdown?: ScoreBreakdown, isOnTime?: boolean, feedback?: Feedback): MarkDayCompletedResult => {
  try {
    const completed = getCompletedDays();
    const wasAlreadyCompleted = completed[dayIndex] === true;

    completed[dayIndex] = true;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(completed));

    // Track completion date (only set once)
    setCompletionDate(dayIndex);

    // Track on-time completion if specified (only set once)
    if (isOnTime && !wasAlreadyCompleted) {
      addOnTimeCompletion(dayIndex);
    }

    const newScore = totalPoints !== undefined ? totalPoints : POINTS_PER_DAY;
    const newStars = stars !== undefined ? stars : 0;
    const newBreakdown = breakdown || {
      relevance: 100,
      correctness: 100,
      depth: 100,
      clarity: 100,
      promptcraft: 100
    };

    // Get previous submission
    const previousSubmission = getDaySubmission(dayIndex);
    const previousScore = previousSubmission?.score || 0;
    const previousBreakdown = previousSubmission?.scoreBreakdown || null;
    const previousStars = previousSubmission?.stars || 0;

    // Calculate delta (how much better this score is)
    const scoreDelta = Math.max(0, newScore - previousScore);

    console.log(`[markDayCompleted] Day ${dayIndex}: previousScore=${previousScore}, newScore=${newScore}, delta=${scoreDelta}`);

    // Save submission if new score is better OR if there's no existing submission
    if (newScore > previousScore || !previousSubmission) {
      saveDaySubmission(dayIndex, {
        score: newScore,
        stars: newStars,
        scoreBreakdown: newBreakdown,
        feedback: feedback
      });
    }

    // Dispatch custom event for same-window updates
    window.dispatchEvent(new CustomEvent('dayCompleted', { detail: { dayIndex } }));

    // Add only the delta to total score
    if (scoreDelta > 0) {
      addScore(scoreDelta);
    }

    return {
      scoreDelta,
      previousBreakdown
    };
  } catch (error) {
    console.error('Error saving completion data:', error);
    return {
      scoreDelta: 0,
      previousBreakdown: null
    };
  }
};

export const getScore = (): number => {
  try {
    const stored = localStorage.getItem(SCORE_KEY);
    return stored ? parseInt(stored, 10) : 0;
  } catch (error) {
    console.error('Error reading score:', error);
    return 0;
  }
};

export const addScore = (points: number): void => {
  try {
    const currentScore = getScore();
    localStorage.setItem(SCORE_KEY, (currentScore + points).toString());
  } catch (error) {
    console.error('Error saving score:', error);
  }
};

export const clearAllCompletions = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(SCORE_KEY);
    localStorage.removeItem(LAST_UNLOCKED_DAY_KEY);
    localStorage.removeItem(DAY_SUBMISSIONS_KEY);
    localStorage.removeItem(COMPLETION_DATES_KEY);
    localStorage.removeItem(ON_TIME_COMPLETIONS_KEY);
    localStorage.removeItem(BADGES_KEY);
    localStorage.removeItem(INFO_POPUP_WELCOME_KEY);
    localStorage.removeItem(INFO_POPUP_LAST_DAY_KEY);
  } catch (error) {
    console.error('Error clearing completion data:', error);
  }
};

export const getDayStars = (dayIndex: number): number => {
  const submission = getDaySubmission(dayIndex);
  return submission?.stars || 0;
};

export const getTotalStars = (): number => {
  const submissions = getDaySubmissionsData();
  return Object.values(submissions).reduce((sum, submission) => sum + submission.stars, 0);
};

export const getBestDayScore = (): number => {
  const submissions = getDaySubmissionsData();
  const scores = Object.values(submissions).map(s => s.score);
  return scores.length > 0 ? Math.max(...scores) : 0;
};

export const getBestDayStars = (): number => {
  const submissions = getDaySubmissionsData();
  const stars = Object.values(submissions).map(s => s.stars);
  return stars.length > 0 ? Math.max(...stars) : 0;
};

export const getLastUnlockedDay = (): number => {
  try {
    const stored = localStorage.getItem(LAST_UNLOCKED_DAY_KEY);
    return stored ? parseInt(stored, 10) : 0;
  } catch (error) {
    console.error('Error reading last unlocked day:', error);
    return 0;
  }
};

export const updateLastUnlockedDay = (day: number): void => {
  try {
    const currentLast = getLastUnlockedDay();
    if (day > currentLast) {
      localStorage.setItem(LAST_UNLOCKED_DAY_KEY, day.toString());
    }
  } catch (error) {
    console.error('Error updating last unlocked day:', error);
  }
};

// Completion dates tracking
const getCompletionDatesData = (): CompletionDatesData => {
  try {
    const stored = localStorage.getItem(COMPLETION_DATES_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Error reading completion dates:', error);
    return {};
  }
};

const setCompletionDate = (dayIndex: number): void => {
  try {
    const dates = getCompletionDatesData();
    if (!dates[dayIndex]) {
      dates[dayIndex] = new Date().toISOString();
      localStorage.setItem(COMPLETION_DATES_KEY, JSON.stringify(dates));
    }
  } catch (error) {
    console.error('Error setting completion date:', error);
  }
};

// On-time completions tracking (days completed on their calendar day)
const getOnTimeCompletions = (): number[] => {
  try {
    const stored = localStorage.getItem(ON_TIME_COMPLETIONS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error reading on-time completions:', error);
    return [];
  }
};

const addOnTimeCompletion = (dayIndex: number): void => {
  try {
    const onTimeList = getOnTimeCompletions();
    // Only add if not already in the list
    if (!onTimeList.includes(dayIndex)) {
      onTimeList.push(dayIndex);
      localStorage.setItem(ON_TIME_COMPLETIONS_KEY, JSON.stringify(onTimeList));
    }
  } catch (error) {
    console.error('Error saving on-time completion:', error);
  }
};

// Calculate current streak based on on-time completions
// Must be called with availableDays from config to properly calculate
export const calculateCurrentStreak = (availableDays: number[]): number => {
  const onTimeCompletions = getOnTimeCompletions();

  if (onTimeCompletions.length === 0) {
    return 0;
  }

  // Find the maximum streak by iterating through availableDays in order
  let maxStreak = 0;
  let currentStreak = 0;

  for (let i = 0; i < availableDays.length; i++) {
    const dayIndex = availableDays[i];
    if (onTimeCompletions.includes(dayIndex)) {
      currentStreak++;
      maxStreak = Math.max(maxStreak, currentStreak);
    } else {
      currentStreak = 0;
    }
  }

  return maxStreak;
};

// Calculate total days completed
export const calculateTotalDaysCompleted = (): number => {
  const completedDays = getCompletedDays();
  return Object.values(completedDays).filter(completed => completed).length;
};

// Calculate genius days (days with 3 stars)
export const calculateGeniusDays = (): number => {
  const submissions = getDaySubmissionsData();
  return Object.values(submissions).filter(submission => submission.stars === 3).length;
};

// Calculate total number of 3-star days (genius days)
export const calculateGenius = (): number => {
  const submissions = getDaySubmissionsData();
  // Count all submissions with 3 stars, regardless of gaps
  return Object.values(submissions).filter(submission => submission.stars === 3).length;
};

// Determine badge tier based on value and requirements
const determineBadgeTier = (value: number, requirements: { bronze: number; silver: number; gold: number }): BadgeTier => {
  if (value >= requirements.gold) return BadgeTier.GOLD;
  if (value >= requirements.silver) return BadgeTier.SILVER;
  if (value >= requirements.bronze) return BadgeTier.BRONZE;
  return BadgeTier.NONE;
};

// Check for new badges without updating storage (for popup display)
export const checkForNewBadges = (totalAvailableDays: number, availableDays: number[]): BadgeCheckResult => {
  const newBadges: NewBadge[] = [];

  // Check Streak badge
  const currentStreak = calculateCurrentStreak(availableDays);
  const currentStreakTier = getBadgeTier(BadgeType.STREAK);
  const newStreakTier = determineBadgeTier(currentStreak, BADGE_REQUIREMENTS[BadgeType.STREAK]);

  if (getTierLevel(newStreakTier) > getTierLevel(currentStreakTier)) {
    newBadges.push({ type: BadgeType.STREAK, tier: newStreakTier });
  }

  // Check Completion badge
  const totalCompleted = calculateTotalDaysCompleted();
  const completionPercentage = totalCompleted / totalAvailableDays;
  const currentCompletionTier = getBadgeTier(BadgeType.COMPLETION);
  const newCompletionTier = determineBadgeTier(completionPercentage, BADGE_REQUIREMENTS[BadgeType.COMPLETION]);

  if (getTierLevel(newCompletionTier) > getTierLevel(currentCompletionTier)) {
    newBadges.push({ type: BadgeType.COMPLETION, tier: newCompletionTier });
  }

  // Check Genius badge
  const genius = calculateGenius();
  const currentGeniusTier = getBadgeTier(BadgeType.GENIUS);
  const newGeniusTier = determineBadgeTier(genius, BADGE_REQUIREMENTS[BadgeType.GENIUS]);

  if (getTierLevel(newGeniusTier) > getTierLevel(currentGeniusTier)) {
    newBadges.push({ type: BadgeType.GENIUS, tier: newGeniusTier });
  }

  return { newBadges };
};

// Save badges to storage (call after popup is dismissed)
export const saveBadges = (badges: NewBadge[]): void => {
  badges.forEach(badge => {
    setBadgeTier(badge.type, badge.tier);
  });
};

// Check and update badges after day completion (legacy - kept for compatibility)
export const checkAndUpdateBadges = (totalAvailableDays: number, availableDays: number[]): BadgeCheckResult => {
  const result = checkForNewBadges(totalAvailableDays, availableDays);
  saveBadges(result.newBadges);
  return result;
};
